import {Injectable} from '@angular/core';
import {LangSrcService} from '../lang-src/lang-src.service';
import {LangTgt} from '../../interfaces/lang-tgt';
import {StorageMap} from '@ngx-pwa/local-storage';
import {Subject} from 'rxjs';
import {LearningModalSameLanguageComponent} from '../../pages/learning/learning-modal-same-language/learning-modal-same-language.component';
import {UserService} from '../user/user.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfigService} from '../config/config.service';
import {EvaluationService} from '../evaluation/evaluation.service';
import {Router} from '@angular/router';
import { PlatformService } from '../platform/platform.service';
import { User } from 'src/app/interfaces/user';
import { AuthService } from '../auth/auth.service';
import { LangTgtService } from '../lang-tgt/lang-tgt.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class RedirectService {

  playSubject: Subject<{
    language: LangTgt;
    forcePlay?: boolean;
  }> = new Subject<{
    language: LangTgt;
    forcePlay?: boolean;
  }>();

  constructor(
    private modal: NgbModal,
    private storage: StorageMap,
    private langSrc: LangSrcService,
    private userSrv: UserService,
    private configSrv: ConfigService,
    private evaluationSrv: EvaluationService,
    private router: Router,
    private plateformSrv: PlatformService,
    private authSrv: AuthService,
    private langTgtSrv: LangTgtService
  ) {
  }

  async getPlatformUrl(toPlatformDashboard?: boolean, toEval?: boolean, langTGT?: string) {
    const token = await this.storage.get('token').toPromise();
    const user = await this.authSrv.getUser();
    const subdomain: string = this.getSubDomain(location.host);

    // Sous-domaine cible : celui du revendeur, sinon app
    const targetSubdomain = user && user.reseller && user.reseller.url ? user.reseller.url : 'app';
    const host = this.buildHost(targetSubdomain);
    const base = `${location.protocol}//${host}/${this.langSrc.current.shortCode}`;

    // Si le token n'existe pas on renvoie sur la page de login
    if (!token) {
      return `${location.protocol}//${this.buildHost('app')}/${this.langSrc.current.shortCode}/auth`;
    }

    // Sur un domaine alias on reste sur le même host : le token est déjà là, pas besoin de relog.
    // Sinon on check si le sous-domaine actuel est bien le sous-domaine cible, sinon on relog le user
    const sameHost = this.isAliasHost() || subdomain === targetSubdomain;

    // Si il faut renvoyer sur le dashboard
    if (toPlatformDashboard) {
      // Si il faut renvoyer l'utilisateur sur le test d'eval
      if (toEval) {
        return `${base}/evaluation/${langTGT}`;
      }

      return sameHost ? `${base}/learning/dashboard` : `${base}/auth/auto/${token}`;
    }

    // Si il faut renvoyer sur le parcours
    return sameHost ? `${base}/games` : `${base}/auth/auto/${token}/games`;
  }

  // Un domaine alias (ex: seriousgame.wefitgroup.com) pointe sur le front sans suivre
  // le schéma de sous-domaines {app|reseller}.{domaine GS} : on ne doit jamais en sortir.
  isAliasHost(): boolean {
    // hostname (sans port) pour ne pas casser le check en dev local (ex: app.globespeaker.local:4200)
    return !location.hostname.endsWith(environment.domain);
  }

  // Host cible pour une redirection : sur un domaine alias on garde le host courant,
  // sinon on construit {sousDomaine}.{domaine sans sous-domaine}
  buildHost(subdomain: string): string {
    if (this.isAliasHost()) {
      return location.host;
    }

    return `${subdomain}.${this.getDomainWithoutSubdomain(location.host)}`;
  }

  async toPlatform(lang: LangTgt, toPlatformDashboard?: boolean) {
    const platformUrl = await this.getPlatformUrl(toPlatformDashboard);

    window.open(platformUrl, '_self');
  }

  async toSection(lang: LangTgt, section: string) {
    const platformUrl = await this.getPlatformUrl();
    window.open(`${platformUrl}?section=${section}`, '_self');
  }

  async toLevel(lang: LangTgt, section: string, level: string) {
    const platformUrl = await this.getPlatformUrl();
    window.open(`${platformUrl}?section=${section}&level=${level}`, '_self');
  }

  async play(params: {
    language: LangTgt;
    forcePlay?: boolean;
  }) {
    // open modal for change language
    if (params.language.code === this.langSrc.current.shortCode) {
      const modal = this.modal.open(LearningModalSameLanguageComponent, {
        backdrop: 'static',
        keyboard: false
      });
      modal.componentInstance.langTgt = params.language;
      modal.result.then(async (redirectToPlatform: boolean) => {
        if (redirectToPlatform) {
          await this.play(params);
        }
      });
      return;
    }

    let redirectToPlatformDashboard = false;

    if (!params.forcePlay) {
      // get current level of user
      const userLevel: any = await this.userSrv.currentLevel(params.language.code);
      redirectToPlatformDashboard = userLevel.playTime > 0;
    }

    // Set platform + force set current langTGT
    this.langTgtSrv.forceSetCurrent(params.language);
    this.plateformSrv.forceSetPlatform(params.language);

    // go to platform
    if (
      !this.configSrv.config.informations.evaluationActive ||
      !params.language.evaluationActive
    ) {
      await this.toPlatform(params.language, redirectToPlatformDashboard);
      return;
    }

    // evaluation test already completed
    const evalUserCompleted = await this.evaluationSrv.completed(params.language.code);

    if (evalUserCompleted) {
      await this.toPlatform(params.language, redirectToPlatformDashboard);
      return;
    }

    // open evaluation test
    await this.goToEvaluationTest(params.language);
  }

  async goToEvaluationTest(langTgt: LangTgt) {
    const platformUrl = await this.getPlatformUrl(true, true, langTgt.code);

    window.open(platformUrl, '_self');
  }

  getDomainWithoutSubdomain(url) {
    const urlParts = url.split('.');

    return urlParts
      .slice(0)
      .slice(-(urlParts.length === 4 ? 3 : 2))
      .join('.');
  }

  getSubDomain(url) {
    const urlParts = url.split('.');

    return urlParts[0];
  }
}
